import { FC, useMemo } from "react";

import DRLogo from "@/assets/icons/DRLogo.svg?react";
import {
  AnimationTextProp,
  TypingAnimation,
} from "@/components/Interface/TypingAnimation/TypingAnimation";

interface HeroSectionProps {
  children?: React.ReactNode;
  textsToAnimate: Array<string>;
}

/**
 * HeroSection - A component that displays a hero section with a logo and a typing animation
 *
 * This component can display a single text string or cycle through multiple strings
 * with customizable typing animations.
 *
 * @example
 * Basic usage with 2 text elements
 * <HeroSection textsToAnimate={["Hello how you doin?", "I am a hero!"]} />
 */
export const HeroSection: FC<HeroSectionProps> = ({ textsToAnimate }) => {
  const animatedTexts: AnimationTextProp[] = useMemo(
    () =>
      textsToAnimate?.map((text, index) => {
        let animationStyle: AnimationTextProp["animationStyle"] = "typeOnly";
        // if not last text, set the animation style to typeAndDelete
        if (index < textsToAnimate.length - 1) {
          animationStyle = "typeAndDelete";
        }
        // if last (or only) text, set the animation style to typeOnly
        else {
          animationStyle = "typeOnly";
        }

        return {
          animationStyle: animationStyle,
          textString: `${text}`,
          textStyle: `text-5xl font-semibold md:text-[56px] ${animationStyle === "typeAndDelete" ? "text-secondary-100" : "text-primary-100"} `,
        };
      }),
    [textsToAnimate],
  );

  return (
    <>
      <DRLogo className="static -ml-6 h-auto w-[161px] pt-8 md:-ml-8 md:w-[248px] md:pt-4" />
      <TypingAnimation content={animatedTexts} />
    </>
  );
};
